/* Myverse — Sticky ATC (mobile)
   Watches the main product ATC button via IntersectionObserver.
   Reveals a bottom bar with title + price + ATC when the main button is out of view.
   Mobile-only — desktop already has Dawn's sticky right column.
*/

class MvStickyAtc extends HTMLElement {
  connectedCallback() {
    // Defer init until product form is hydrated
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Small delay so Dawn's product-info component is in DOM
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    this.bar = this.querySelector('[data-mv-sticky-bar]');
    this.priceEl = this.querySelector('[data-mv-sticky-price]');
    this.button = this.querySelector('[data-mv-sticky-button]');

    // Find the main product ATC — Dawn id pattern: ProductSubmitButton-{section_id}
    this.mainButton = document.querySelector('product-info [name="add"], product-info .product-form__submit');
    this.mainForm = this.mainButton ? this.mainButton.closest('form') : null;

    if (!this.bar || !this.mainButton || !this.mainForm) {
      // Nothing to do (likely not a product page, or product-info missing)
      this.hidden = true;
      return;
    }

    // Mobile-only — bail on wide screens (CSS also enforces this, JS skips wasted work)
    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.observer = null;

    this.setupObserver();
    this.mediaQuery.addEventListener('change', () => this.setupObserver());

    // Click sticky button → submit the main form (preserves all Dawn cart logic)
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof this.mainForm.requestSubmit === 'function') {
        this.mainForm.requestSubmit();
      } else {
        this.mainForm.submit();
      }
    });

    // Keep button label + state in sync with variant changes
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('variant-selects input, variant-radios input, .product-form__input input')) {
        // Defer to let Dawn's variant update finish
        setTimeout(() => this.syncState(), 50);
      }
    });
  }

  setupObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (!this.mediaQuery.matches) {
      this.bar.classList.remove('is-visible');
      this.bar.setAttribute('aria-hidden', 'true');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldShow = !entry.isIntersecting;
        this.bar.classList.toggle('is-visible', shouldShow);
        this.bar.setAttribute('aria-hidden', String(!shouldShow));
      },
      { threshold: 0, rootMargin: '0px 0px -20px 0px' }
    );
    this.observer.observe(this.mainButton);
  }

  syncState() {
    if (!this.mainButton || !this.button) return;
    const disabled = this.mainButton.hasAttribute('disabled');
    this.button.disabled = disabled;
    const mainText = (this.mainButton.textContent || '').trim();
    if (mainText && this.button.querySelector('span')) {
      this.button.querySelector('span').textContent = mainText;
    }

    // Price — read from Dawn's price block (it's updated by Dawn on variant change)
    const livePrice = document.querySelector('product-info .price__regular .price-item--regular, product-info .price .price-item');
    if (livePrice && this.priceEl) {
      this.priceEl.textContent = livePrice.textContent.trim();
    }
  }
}

if (!customElements.get('mv-sticky-atc')) {
  customElements.define('mv-sticky-atc', MvStickyAtc);
}
